import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { Box, Text, SelectOption, Switch, Button } from "@showitinc/showit-wc";

import Input from "../../../base/Input";
import useOpenAccount, { PATH_THIRD_PARTY } from "../../../../hooks/useOpenAccount";
import API from "../../../../service/API";
import { getAccountData, isContributing as getIsContributing } from "../../../../hooks/query/useAccount";
import { INSTAGRAM_SOURCES } from "../../../../constants/QueryKeys";
import SocialGridConstants from "../../../../constants/SocialGridConstants";
import { INPUT_TYPE_NUMBER } from "../../../../constants/ElementConstants";
import { gatherProps } from "../../../../utils/Merge/MergeUtils.mjs";
import {
  SOCIAL_GRID_ALIGNMENT,
  SOCIAL_GRID_AUTO_GAP,
  SOCIAL_GRID_GAP,
  SOCIAL_GRID_OVERFLOW,
  SOCIAL_GRID_ROWS,
} from "../../../../constants/MergeConstants";
import { elementSyncUtils } from "../../../../utils/SyncUtils";
import AlignOptions from "../../../../components/elementproperties/AlignOptions";
import { formatPropUpdate } from "../../../../utils/PropertyUtils";
import SplitMergeWrapper from "../../../base/SplitMergeWrapper";
import { prepareInputUtils } from "../../../../utils/InputUtils";
import { SELECT } from "../../../../constants/InputConstants.mjs";

const defaults = fromJS({
  overflow: 0,
  autoGap: 0,
  gap: 5,
  rows: 1,
  alignment: "center",
});

const SocialGridProperties = ({ onPropUpdate, element, layout, isLocked, preparedElement }) => {
  const [openAccountProfile] = useOpenAccount({ path: PATH_THIRD_PARTY, windowTarget: "_blank" });

  const account = element.getIn(["content", "account"], "");
  const allowVideo = element.getIn(["content", "allowVideo"], 1);
  const allowCarousel = element.getIn(["content", "allowCarousel"], 1);
  const allowImage = element.getIn(["content", "allowImage"], 1);
  const allowCaption = element.getIn(["content", "allowCaption"], 1);
  const allowedTypes = allowVideo + allowCarousel + allowImage;
  const token = getAccountData()?.token;
  const isContributing = getIsContributing();
  const { isLoading: socialAccountsLoading = true, data: socialAccounts = [] } = useQuery({
    queryKey: [INSTAGRAM_SOURCES],
    queryFn: () => API.get(SocialGridConstants.LINKED_SOCIAL_ACCOUNTS_URL, {}, token),
    enabled: true,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: false,
    refetchOnMount: true,
    staleTime: Infinity,
  });
  const hasInactiveAccounts =
    socialAccounts.length && !!socialAccounts.filter((acc) => acc.status === 2).length;

  const _onPropChange = (propInfo) => !isLocked && onPropUpdate && onPropUpdate(propInfo);

  const _onCheckChange = (propInfo) =>
    _onPropChange({ ...propInfo, value: propInfo.value ? 1 : 0 });

  const _layoutInputs = () => {
    const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
      element,
      _onPropChange,
      layout
    );

    const elemProps = defaults.merge(
      gatherProps(preparedElement, layout, [
        SOCIAL_GRID_ALIGNMENT,
        SOCIAL_GRID_AUTO_GAP,
        SOCIAL_GRID_GAP,
        SOCIAL_GRID_OVERFLOW,
        SOCIAL_GRID_ROWS,
      ])
    );

    const { canMerge, prepareValue, isSyncMixed } = prepareInputUtils(
      layout,
      preparedElement,
      elemProps
    );

    const rows = prepareValue(SOCIAL_GRID_ROWS);
    const autoGap = prepareValue(SOCIAL_GRID_AUTO_GAP) === 1;
    const overflow = prepareValue(SOCIAL_GRID_OVERFLOW) === 1;

    return (
      <>
        {+rows === 1 && (
          <>
            {!overflow && (
              <SplitMergeWrapper
                layout={layout}
                mixed={isSyncMixed(SOCIAL_GRID_AUTO_GAP)}
                sync={isSynced(SOCIAL_GRID_AUTO_GAP)}
                toggleSync={() => toggleSyncedProp(SOCIAL_GRID_AUTO_GAP)}
              >
                <Switch
                  context={canMerge(SOCIAL_GRID_AUTO_GAP)}
                  title="Space out post images evenly"
                  label="Auto-space Posts"
                  disabled={isLocked}
                  checked={autoGap}
                  onSIChange={(e) =>
                    _onCheckChange(
                      formatPropUpdate(getPropName(SOCIAL_GRID_AUTO_GAP), e.target.checked, autoGap)
                    )
                  }
                />
              </SplitMergeWrapper>
            )}
            {!autoGap && (
              <SplitMergeWrapper
                layout={layout}
                mixed={isSyncMixed(SOCIAL_GRID_OVERFLOW)}
                sync={isSynced(SOCIAL_GRID_OVERFLOW)}
                toggleSync={() => toggleSyncedProp(SOCIAL_GRID_OVERFLOW)}
              >
                <Switch
                  context={canMerge(SOCIAL_GRID_OVERFLOW)}
                  label="Allow Overflow"
                  title="Allow posts to overflow the Social Grid"
                  disabled={isLocked}
                  checked={overflow}
                  onSIChange={(e) =>
                    _onCheckChange(
                      formatPropUpdate(
                        getPropName(SOCIAL_GRID_OVERFLOW),
                        e.target.checked,
                        overflow
                      )
                    )
                  }
                />
              </SplitMergeWrapper>
            )}
          </>
        )}

        <SplitMergeWrapper
          layout={layout}
          mixed={isSyncMixed(SOCIAL_GRID_ROWS)}
          sync={isSynced(SOCIAL_GRID_ROWS)}
          toggleSync={() => toggleSyncedProp(SOCIAL_GRID_ROWS)}
        >
          <Input
            context={canMerge(SOCIAL_GRID_ROWS)}
            type={INPUT_TYPE_NUMBER}
            label="Rows"
            min="1"
            max="100"
            disabled={isLocked}
            value={rows}
            onSIBeforeChange={(e) =>
              _onPropChange(formatPropUpdate(getPropName(SOCIAL_GRID_ROWS), e.target.value, rows))
            }
            onSIInput={(e) =>
              _onPropChange(
                formatPropUpdate(getPropName(SOCIAL_GRID_ROWS), e.target.value, rows, null, false)
              )
            }
          />
        </SplitMergeWrapper>

        {(+rows > 1 || !autoGap) && (
          <SplitMergeWrapper
            layout={layout}
            mixed={isSyncMixed(SOCIAL_GRID_GAP)}
            sync={isSynced(SOCIAL_GRID_GAP)}
            toggleSync={() => toggleSyncedProp(SOCIAL_GRID_GAP)}
          >
            <Input
              context={canMerge(SOCIAL_GRID_GAP)}
              type={INPUT_TYPE_NUMBER}
              label="Image Gap"
              min="0"
              units="px"
              disabled={isLocked}
              value={prepareValue(SOCIAL_GRID_GAP)}
              onSIBeforeChange={(e) =>
                _onPropChange(
                  formatPropUpdate(
                    getPropName(SOCIAL_GRID_GAP),
                    e.target.value,
                    prepareValue(SOCIAL_GRID_GAP)
                  )
                )
              }
              onSIInput={(e) =>
                _onPropChange(
                  formatPropUpdate(
                    getPropName(SOCIAL_GRID_GAP),
                    e.target.value,
                    prepareValue(SOCIAL_GRID_GAP),
                    null,
                    false
                  )
                )
              }
            />
          </SplitMergeWrapper>
        )}

        {(+rows > 1 || (!autoGap && !overflow)) && (
          <SplitMergeWrapper
            mixed={isSyncMixed(SOCIAL_GRID_ALIGNMENT)}
            layout={layout}
            sync={isSynced(SOCIAL_GRID_ALIGNMENT)}
            toggleSync={() => toggleSyncedProp(SOCIAL_GRID_ALIGNMENT)}
          >
            <AlignOptions
              alignment={prepareValue(SOCIAL_GRID_ALIGNMENT)}
              context={canMerge(SOCIAL_GRID_ALIGNMENT)}
              disabled={isLocked}
              onSelect={(e) =>
                _onPropChange(
                  formatPropUpdate(
                    getPropName(SOCIAL_GRID_ALIGNMENT),
                    e.target.value,
                    prepareValue(SOCIAL_GRID_ALIGNMENT)
                  )
                )
              }
              propName={getPropName(SOCIAL_GRID_ALIGNMENT)}
              useJustify={false}
            />
          </SplitMergeWrapper>
        )}
      </>
    );
  };

  return (
    <Box pt="2">
      {socialAccounts.length > 0 && (
        <Input
          inputType={SELECT}
          label="Account"
          disabled={isLocked}
          reserveContext
          shift="left"
          value={
            socialAccounts.find(({ integrationId }) => integrationId === account) ? account : ""
          }
          onSISelect={(e) =>
            _onPropChange(
              formatPropUpdate(
                "content.account",
                e.target.value,
                socialAccounts.find(({ integrationId }) => integrationId === account) ? account : ""
              )
            )
          }
        >
          <SelectOption value="">Choose account</SelectOption>
          {socialAccounts.map(
            ({ integrationId, name, status }) =>
              status > 0 && (
                <SelectOption key={integrationId} value={integrationId}>
                  {`@${name}${status === 2 ? " (inactive)" : ""}`}
                </SelectOption>
              )
          )}
        </Input>
      )}

      {!socialAccountsLoading &&
        (!socialAccounts.length || hasInactiveAccounts) &&
        !isContributing && (
          <Box mt="2" px="2">
            <Button
              variant="outline"
              fullWidth
              onSIClick={openAccountProfile}
              className="sg-button"
              iconPrefix="socialIG"
              iconSize="20"
            >
              {hasInactiveAccounts ? "Reconnect" : "Connect"} Instagram
            </Button>
          </Box>
        )}

      {!socialAccountsLoading && isContributing && (
        <Box mb="3">
          <Text variant="small">
            Instagram connections are not editable while you are contributing.
          </Text>
        </Box>
      )}

      <Switch
        label="Video Posts"
        title="Show video type posts"
        disabled={isLocked}
        checked={allowVideo}
        onSIChange={(e) =>
          _onCheckChange(formatPropUpdate("content.allowVideo", e.target.checked, allowVideo))
        }
      />
      <Switch
        label="Carousel Posts"
        title="Show carousel type posts"
        disabled={(allowCarousel && allowedTypes < 2) || isLocked}
        checked={allowCarousel}
        onSIChange={(e) =>
          _onCheckChange(formatPropUpdate("content.allowCarousel", e.target.checked, allowCarousel))
        }
      />
      <Switch
        label="Image Posts"
        title="Show image type posts"
        disabled={(allowImage && allowedTypes < 2) || isLocked}
        checked={allowImage}
        onSIChange={(e) =>
          _onCheckChange(formatPropUpdate("content.allowImage", e.target.checked, allowImage))
        }
      />
      <Switch
        disabled={isLocked}
        label="Captions On Hover"
        title="Use Instagram captions for hover title"
        checked={allowCaption}
        onSIChange={(e) =>
          _onCheckChange(formatPropUpdate("content.allowCaption", e.target.checked, allowImage))
        }
      />
      {_layoutInputs()}

      {socialAccounts.length && !isContributing ? (
        <Box mt="2" px="2">
          <Button
            variant="outline"
            fullWidth
            onSIClick={openAccountProfile}
            className="sg-button"
            iconPrefix="socialIG"
            iconSize="20"
          >
            Manage Accounts
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default SocialGridProperties;
